# Display the SEEK Profile Panel

The SEEK Profile Panel allows hirers to view a candidate’s SEEK Profile in flow.
It should be shown when a hirer clicks the View SEEK Profile link,
and may be displayed in a tab or drawer.

## Before you begin

Ensure that you have reviewed the [initial setup for SEEK Profile](/use-cases/seek-profile#before-you-begin).

Review the [placement guidelines](/use-cases/seek-profile/summary#before-you-begin) for the other components that make up this feature.

Review the [panels guide](/introduction/panels) for general information on how SEEK panels work.

### Panel placement

The SEEK Profile Panel is built to be responsive and can accommodate placement at various widths.

Consider the context in which the SEEK Profile Panel will be displayed.
Generally, there are two:

1. Candidate or applicant list: in this context, the panel can be displayed in a drawer.
1. Candidate details: in this context, the panel can be displayed in a tab, drawer or its own page.

Ultimately, the panel should be displayed in a way that is consistent with the existing UX patterns in your software.

Here are some examples of the panel displayed in different contexts:

<img
  src="/assets/seek-profile/panel-drawer.png"
  alt="Candidate Profile Panel in a drawer from a candidate list"
  width="100%"
/>

<img
  src="/assets/seek-profile/panel-tab.png"
  alt="Candidate Profile Panel in a tab on a candidate details page"
  width="100%"
/>

## Step 1: Include the panel

Add the following script tag to the page where the SEEK Profile Panel will be displayed:

```html
<script
  type="text/javascript"
  src="https://integration.seek.com/panels/SeekApi.js"
></script>
```

## Step 2: Render the panel

The `SeekApi.render` function renders an instance of the panel in the specified DOM element.

```javascript
SeekApi.render(containerNode, 'seekProfile', props);
```

- `containerNode`:
  The DOM element to render the panel into.
- `'seekProfile'`:
  The name of the panel to render.
- `props`:
  [Props](#props) for rendering the panel.

### Props

| Prop                | Type                                          | Description                                                                                                                                                                   |
| :------------------ | :-------------------------------------------- | :---------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
| `getAuthToken`      | `() => Promise<string>`                       | Function to call to retrieve the [browser token](#step-3-handle-browser-token-requests) to use when calling the SEEK API.                                                     |
| `candidate`         | `object`                                      | Object with **`email`** (string): the candidate’s email, same identifier you use with SEEK candidate APIs (e.g. [`seekCandidateSummaries`](/use-cases/seek-profile/summary)). |
| `context`           | <p>`string`</p><p><small>optional</small></p> | The context in which the candidate profile is being viewed. Supported values are `APPLICANT_LIST` and `CANDIDATE_DATABASE`.                                                   |
| `applicationStatus` | <p>`object`</p><p><small>optional</small></p> | For SEEK jobs only, in the `APPLICANT_LIST` context, provide the status of the candidate’s application.                                                                       |
| `locale`            | <p>`string`</p><p><small>optional</small></p> | Specifies the locale to display content in, e.g. `en-AU`. Supported locales are outlined in the [content localisation] documentation.                                         |

### Example

```javascript
SeekApi.render(containerNode, 'seekProfile', {
  getAuthToken,
  candidate: {
    email: 'candidate@example.com'
  }
});
```

[content localisation]: /graphql/in-practice#content-localisation

{/* TODO: need to define shape of `position` prop */}
{/* TODO: need to define possible values for `applicationStatus` prop */}
{/* TODO: demo implementation of the panel with dummy data */}

## Step 3: Handle browser token requests

```mermaid Panel authentication
sequenceDiagram
  autonumber
  participant Panel as SEEK Profile Panel
  participant Frontend as Partner frontend
  participant Backend as Partner backend
  participant Auth as /auth/token
  participant Graph as /graphql

  Panel->>Frontend: Call `getAuthToken`
  Frontend->>Backend: Request browser token
  Backend->Backend: Authenticate and authorize user
  Backend->>Auth: Request browser token
  Backend-->>Frontend: Return browser token
  Frontend-->>Panel: Return from `getAuthToken`
  Panel->>Graph: Make requests to the GraphQL endpoint
```

1. The panel loads and invokes the `getAuthToken` function passed to it.

2. Your frontend requests a browser token from your backend.

   The `getAuthToken` function should request a new token for the hirer ID in `positionProfile.positionOrganizations`.
   If a user switches to a different SEEK hirer account,
   your software should re-render the panel with the new hirer ID in `positionProfile.positionOrganizations`,
   and ensure that subsequent invocations of `getAuthToken` will request a token for the new hirer ID.

3. Your backend authenticates and authorizes the user.

   Your software is responsible for verifying that the user is authorized to access a given hirer ID.
   A user must not be able to request a browser token for an arbitrary organization that they do not belong to.

4. Your backend [requests a browser token from the SEEK API] for the appropriate hirer ID and both `query:seek-candidate-profiles query:organizations` scopes.

5. Your backend responds with the browser token.

6. Your frontend returns the browser token from the `getAuthToken` function.

7. The panel can now make requests to the GraphQL endpoint.

```http
POST https://graphql.seek.com/auth/token HTTP/2
Authorization: Bearer PARTNER_TOKEN_HERE
Content-Type: application/json
User-Agent: YourPartnerService/1.2.3

{
  "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",
  "scope": "query:seek-candidate-profiles query:organizations",
  "userId": "317665"
}
```

[requests a browser token from the seek api]: /auth/browser-tokens
